import { currentDevelopmentUIState } from './dev-ui-state';
import { isRemoteEAPDevMode } from './is-dev-mode';

export interface Board {
  id: string;
  referenceId: string;
  title: string;
}

export interface Space {
  id: string;
  name: string;
  key: string;
}

export interface PlatformFeatureFlag {
  id: string;
  value: string;
}

const proxyThroughGateway = (url: string) => {
  if (!isRemoteEAPDevMode()) {
    return url;
  }

  const target = new URL(window.location.origin);
  target.pathname += `/gateway/api/ex/confluence/${currentDevelopmentUIState.cloudId}`;
  target.pathname += url;

  return target.href;
};

export async function fetchSpaces(
  spaceNamePattern = currentDevelopmentUIState.containerName
): Promise<Space[]> {
  const url = proxyThroughGateway('/cgraphql');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
    body: JSON.stringify({
      operationName: 'spacesInSite',
      variables: { spaceNamePattern },
      query: `
        query spacesInSite {
          spaces(spaceNamePattern: "${spaceNamePattern}") {
            nodes {
              id
              name
              key
            }
          }
        }
      `,
    }),
  });

  const { data } = await response.json();

  return data?.spaces?.nodes ?? [];
}

export async function fetchBoards(
  spaceKey = currentDevelopmentUIState.containerKey
): Promise<Board[]> {
  const url = proxyThroughGateway('/cgraphql');

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
    body: JSON.stringify({
      operationName: 'fetchBoards',
      query: `
        query fetchBoards {
          search(cql: "type=whiteboard and space.key='${spaceKey}'") {
            nodes {
              content {
                id
                referenceId
                title
              }
            }
          }
        }
      `,
    }),
  });

  const { data } = await response.json();

  return data?.search?.nodes?.map(({ content }: { content: Board }) => content) ?? [];
}

export async function createBoard(spaceKey: string): Promise<{
  id: string;
  referenceId: string;
}> {
  const url = proxyThroughGateway('/cgraphql');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
    body: JSON.stringify({
      operationName: 'createWhiteboard',
      variables: { spaceKey },
      query:
        'mutation createWhiteboard($spaceKey: String!) {\n  createContentContextual(input: {spaceKey: $spaceKey, status: CURRENT, type: "whiteboard"}) {\n    id\n    referenceId\n    __typename\n  }\n}\n',
    }),
  });

  const { data } = await response.json();
  return data?.createContentContextual;
}

export async function deleteBoard(contentId: string) {
  const url = proxyThroughGateway('/cgraphql');
  await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
    body: JSON.stringify({
      operationName: 'deleteWhiteboard',
      variables: { contentId },
      query:
        'mutation deleteWhiteboard($contentId: ID) {\n  deleteContent(\n    contentId: $contentId\n    action: MOVE_TO_TRASH\n  ){\n   contentId\n   __typename\n  }\n}\n',
    }),
  });
}

export async function fetchPlatformFeatureFlags(): Promise<PlatformFeatureFlag[]> {
  const url = proxyThroughGateway('/cgraphql');
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      credentials: 'include',
    },
    body: JSON.stringify({
      query: `
        query SessionDataQuery {
          platformFeatureFlags: featureFlags(prefix: PLATFORM, extraTargeting: true, first: 500) {
            nodes {
              id
              value
            }
          }
        }
      `,
    }),
  });

  const { data } = await response.json();

  return data?.platformFeatureFlags?.nodes ?? [];
}
